import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { of } from "rxjs";
import {
  catchError,
  concatMap,
  filter,
  map,
  switchMap,
  tap,
  withLatestFrom,
} from "rxjs/operators";
import {
  coursesAdminInitSubscriptions,
  coursesAdminListLoad,
  coursesAdminListLoadSuccess,
  coursesAdminReset,
  coursesAdminSearch,
  loadTeachersFailure,
} from "../../actions/admin/course-admin.actions";
import { AdminService } from "src/app/shared/services/admin/admin.service";
import {
  CourseAdmin,
  PaginationData,
} from "src/app/shared/interfaces/orgadmin/course-organization";
import { AdminCourseList } from "../../reducers/admin/course-admin.reducer";
import { Store } from "@ngrx/store";
import { adminSelector } from "../../selectors/course-admin.selectors";
import { packageListLoad } from "../../actions/packages/packages.actions";
import { invitedTeachersListLoad } from "../../actions/admin/invited-teachers.actions";
import { LoaderService } from "src/app/modules/orgadmin/services/organizations-loader/loader.service";

@Injectable()
export class CourseAdminEffect {
  coursesAdminInitSubscriptions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(coursesAdminInitSubscriptions),
      withLatestFrom(this.store.select(adminSelector)),
      filter(
        ([action, state]) =>
          state.search !== action.search ||
          state.orgId !== action.organizationId ||
          state.active.status !== "SUCCESS" ||
          state.inactive.status !== "SUCCESS",
      ),
      switchMap(([action]) => [
        coursesAdminReset({ active: true }),
        coursesAdminReset({ active: false }),
        coursesAdminListLoad({
          id: action.organizationId,
          page: 1,
          active: true,
          ...(action.search ? { search: action.search } : {}),
        }),
        coursesAdminListLoad({
          id: action.organizationId,
          page: 1,
          active: false,
          ...(action.search ? { search: action.search } : {}),
        }),
        invitedTeachersListLoad({
          id: action.organizationId,
          active: "False",
        }),
        packageListLoad({
          id: action.organizationId,
        }),
      ]),
    ),
  );

  getCoursesAdminList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(coursesAdminListLoad),
      concatMap((param) => {
        return this.adminService
          .getSubscriptions(
            param.id,
            param.page,
            param.size,
            param.search,
            param.active,
          )
          .pipe(
            map((coursesList: PaginationData<CourseAdmin> | CourseAdmin[]) =>
              coursesAdminListLoadSuccess({
                coursesList: this.normalizeCoursesList(coursesList),
                active: param.active,
                orgId: param.id,
                search: param.search,
              }),
            ),
            tap(() => {
              if (param.active)
                this.orgadminLoader.setActiveSubscriptionsLoader(false);
              else this.orgadminLoader.setInactiveSubscriptionsLoader(false);
            }),
            catchError(({ error }) => of(loadTeachersFailure(error))),
          );
      }),
    ),
  );
  getCoursesSearch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(coursesAdminSearch),
      concatMap((param) => [
        coursesAdminReset({ active: param.active }),
        coursesAdminListLoad(param),
      ]),
    ),
  );

  constructor(
    private adminService: AdminService,
    private actions$: Actions,
    private store: Store<{ course: AdminCourseList }>,
    private orgadminLoader: LoaderService,
  ) {}

  private normalizeCoursesList(
    coursesList: PaginationData<CourseAdmin> | CourseAdmin[],
  ): PaginationData<CourseAdmin> {
    if (!Array.isArray(coursesList)) {
      return coursesList;
    }

    return {
      results: coursesList,
      next: 0,
      page_count: 1,
      previous: 0,
      result_count: coursesList.length,
    };
  }
}
